<template>
  <div id="partnerCompanyDetails">
    <div class="mt-8">
      <h4>{{ t('PartnerCompany.logo') }}</h4>
      <img v-if="partnerCompany.logoUrl" class="max-w-[400px]" :src="partnerCompany.logoUrl" data-testid="partner-logo" />
    </div>

    <div class="mt-8">
      <h4>{{ t('PartnerCompany.webLogo') }}</h4>
      <img
        v-if="partnerCompany.webLogoUrl"
        class="h-[400px] w-[400px] object-contain"
        :src="partnerCompany.webLogoUrl"
        data-testid="partner-web-logo"
      />
    </div>

    <div class="mt-8">
      <h4>{{ t('PartnerCompany.name') }}</h4>
      {{ partnerCompany.displayName }}
    </div>

    <div v-if="partnerCompany.website" class="mt-8">
      <h4>{{ t('PartnerCompany.website') }}</h4>
      <a :href="partnerCompany.website" data-testid="partner-website-link">
        {{ partnerCompany.website }}
      </a>
    </div>

    <div class="mt-8">
      <h4>{{ t('PartnerCompany.descriptionDe') }}</h4>
      {{ partnerCompany.descriptionDe }}
    </div>

    <div class="mt-8">
      <h4>{{ t('PartnerCompany.descriptionEn') }}</h4>
      {{ partnerCompany.descriptionEn }}
    </div>

    <div class="mt-8">
      <h4>{{ t('PartnerCompany.interests') }}</h4>
      <div class="chips-list">
        <div v-for="interest in partnerCompany.interests" :key="interest.id" class="chip" label color="primary">
          {{ interest.text }}
        </div>
      </div>
    </div>

    <div v-if="partnerCompany.hasFoodBooths" class="mt-8">
      <h4>{{ t('PartnerCompany.foodTypes') }}</h4>
      <div class="chips-list">
        <div v-for="foodType in partnerCompany.foodTypes" :key="`foodType-${foodType}`" class="chip" label color="primary">
          <SvgIcon :path="getFoodTypeIcon(foodType)" class="mr-1" />
          {{ foodType }}
        </div>
      </div>
    </div>

    <hr class="mt-8" />

    <h4>{{ t('PartnerCompany.socialMediaLinks') }}</h4>

    <div v-for="sm in socialMedia" :key="sm.linkType">
      <h4>{{ t(`PartnerCompany.${sm.linkType}`) }}</h4>
      <a :href="sm.link" data-testid="partner-social-link">{{ sm.link }}</a>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import type { PartnerCompany, PartnerCompanyFragment } from '@/gql/myomr'
import getFoodTypeIcon from '@/helpers/getFoodTypeIcon'

const { t } = useI18n()

const props = defineProps<{
  partnerCompany: PartnerCompany
}>()

const socialMediaLinkTypes: (keyof PartnerCompanyFragment)[] = ['twitter', 'facebook', 'youtube', 'instagram', 'linkedinUrl']

const socialMedia = computed(() => {
  return socialMediaLinkTypes
    .map((linkType) => {
      const link = props.partnerCompany[linkType] as string | null
      if (link && link.length) return { linkType, link }
    })
    .flatMap((link) => (link ? [link] : []))
})
</script>

<style scoped>
@reference '@ramp106/omrjs-config-tailwind/styles/index.css';

a {
  @apply txt-body-s-semibold;
  text-decoration: none;
}

h4 {
  margin-bottom: calc(var(--spacing) * 2.5);
  color: var(--text-color-main) !important;
  @apply txt-body-m-bold;
}
</style>
